"use client";

import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Users, Loader2 } from "lucide-react";
import { API_ENDPOINTS } from "../../../constants/config";
import { toast } from "react-hot-toast";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function EmployeeSalarySummary({ employeeType: employeeTypeProp } = {}) {
  const outletContext = useOutletContext();
  const employeeType = employeeTypeProp || outletContext?.employeeType || "teacher";
  const isStaff = employeeType === "staff";
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const url = isStaff ? API_ENDPOINTS.staffSalarySummary(month, year) : API_ENDPOINTS.teacherSalarySummary(month, year);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setSummary(data.data || []);
      else setSummary([]);
    } catch (e) {
      toast.error("Failed to load summary");
      setSummary([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [month, year, employeeType]);

  const formatMoney = (n) => (n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—");
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
              <Users className="h-5 w-5 text-[#0263cc]" />
              Employee Salary Summary
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={month} onValueChange={setMonth}><SelectTrigger className="w-[120px] bg-white"><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
              <Select value={year} onValueChange={setYear}><SelectTrigger className="w-[90px] bg-white"><SelectValue /></SelectTrigger><SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-1">Overview of employee salary structure and (optional) monthly slip summary.</p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">Employee ID</TableHead>
                  <TableHead className="font-semibold">Employee Name</TableHead>
                  <TableHead className="font-semibold">Basic Salary</TableHead>
                  <TableHead className="font-semibold">Allowances</TableHead>
                  <TableHead className="font-semibold">Effective From</TableHead>
                  <TableHead className="font-semibold">Working Days</TableHead>
                  <TableHead className="font-semibold">Present</TableHead>
                  <TableHead className="font-semibold">Net Salary</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500">No salary configs found. Add in Salary Config tab.</TableCell></TableRow>
                ) : (
                  summary.map((row) => (
                    <TableRow key={row.staffId || row.teacherId} className="hover:bg-slate-50/50">
                      <TableCell className="font-medium">{row.employeeCode || "—"}</TableCell>
                      <TableCell>{row.employeeName || "—"}</TableCell>
                      <TableCell>{formatMoney(row.basicSalary)}</TableCell>
                      <TableCell>{formatMoney(row.allowances)}</TableCell>
                      <TableCell>{row.effectiveFrom ? new Date(row.effectiveFrom).toLocaleDateString() : "—"}</TableCell>
                      <TableCell>{row.workingDays ?? "—"}</TableCell>
                      <TableCell>{row.presentDays ?? "—"}</TableCell>
                      <TableCell className="font-medium">{formatMoney(row.netSalary)}</TableCell>
                      <TableCell>{row.status ? <Badge variant="outline">{row.status}</Badge> : "—"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
